'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { getWebSubscriptionsAvailability } from '@/hotdesk-ui/services/subscription-availability.service';
export type SubscriptionAvailabilityStatus = 'pending' | 'available' | 'unavailable';
type SubscriptionAvailabilityContextValue = {
  status: SubscriptionAvailabilityStatus;
  /** Re-fetch RC and update status; returns whether subscriptions are available. */
  refresh: () => Promise<boolean>;
};
const SubscriptionAvailabilityContext = createContext<SubscriptionAvailabilityContextValue | null>(null);
export function SubscriptionAvailabilityProvider({
  languageCode,
  children
}: {
  languageCode: string;
  children: ReactNode;
}) {
  const [status, setStatus] = useState<SubscriptionAvailabilityStatus>('pending');
  const refresh = useCallback(async (): Promise<boolean> => {
    setStatus('pending');
    try {
      const ok = await getWebSubscriptionsAvailability(languageCode);
      setStatus(ok ? 'available' : 'unavailable');
      return ok;
    } catch {
      setStatus('unavailable');
      return false;
    }
  }, [languageCode]);
  useEffect(() => {
    let cancelled = false;
    setStatus('pending');
    getWebSubscriptionsAvailability(languageCode).then(ok => {
      if (!cancelled) {
        setStatus(ok ? 'available' : 'unavailable');
      }
    }).catch(() => {
      if (!cancelled) {
        setStatus('unavailable');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [languageCode]);
  const value = useMemo(() => ({
    status,
    refresh
  }), [status, refresh]);
  return <SubscriptionAvailabilityContext.Provider value={value} data-sentry-element="unknown" data-sentry-component="SubscriptionAvailabilityProvider" data-sentry-source-file="SubscriptionAvailabilityContext.tsx">
            {children}
        </SubscriptionAvailabilityContext.Provider>;
}
export function useSubscriptionAvailability(): SubscriptionAvailabilityContextValue {
  const ctx = useContext(SubscriptionAvailabilityContext);
  if (!ctx) {
    throw new Error('useSubscriptionAvailability must be used within SubscriptionAvailabilityProvider');
  }
  return ctx;
}